import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles, ShoppingCart, History, Loader2, Info } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { collection, query, where, getDocs, db, orderBy, onSnapshot } from '../firebase';

export const PointsPage: React.FC<{ isDark: boolean }> = ({ isDark }) => {
    const { user, profile } = useUser();
    const navigate = useNavigate();
    const [transactions, setTransactions] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [timeLeft, setTimeLeft] = useState<{ h: number; m: number; s: number } | null>(null);
    const [spentPoints, setSpentPoints] = useState(0);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (!user) {
            setLoading(false);
            return;
        }

        const q = query(
            collection(db, 'pointsTransactions'),
            where('userId', '==', user.uid),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const txs = snapshot.docs.map(doc => {
                const data = doc.data() as any;
                return {
                    id: doc.id,
                    ...data,
                    date: data.createdAt?.toDate() || new Date()
                };
            });
            setTransactions(txs);

            // Calculate total spent points from history for UI (if not directly in profile)
            const spent = txs.filter((t: any) => t.type === 'spend').reduce((sum, t: any) => sum + (t.points || 0), 0);
            setSpentPoints(spent);

            setLoading(false);
        }, (err) => {
            console.error("Error fetching transactions:", err);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [user]);

    useEffect(() => {
        if (!profile?.lastPointsEarnedAt) {
            setTimeLeft(null);
            return;
        }

        const lastEarned = profile.lastPointsEarnedAt.toDate ? profile.lastPointsEarnedAt.toDate() : new Date(profile.lastPointsEarnedAt);
        const cooldownEnd = new Date(lastEarned.getTime() + 48 * 60 * 60 * 1000);

        const calcTime = () => {
            const now = new Date();
            const diff = cooldownEnd.getTime() - now.getTime();
            if (diff <= 0) {
                setTimeLeft(null);
                return;
            }
            const h = Math.floor(diff / (1000 * 60 * 60));
            const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const s = Math.floor((diff % (1000 * 60)) / 1000);
            setTimeLeft({ h, m, s });
        };

        calcTime();
        const int = setInterval(calcTime, 1000);
        return () => clearInterval(int);
    }, [profile]);

    return (
        <div className={`min-h-screen w-full pb-[72px] pt-28 md:pt-32 animate-fade-in ${isDark ? 'bg-[#0f1115] text-white' : 'bg-[#f8f9ff] text-slate-800'}`} style={{ direction: 'rtl' }}>

            {/* Header */}
            <div className="flex items-center justify-between px-6 mb-6">
                <button onClick={() => navigate(-1)} className={`p-2.5 rounded-full transition-transform active:scale-90 shadow-sm border ${isDark ? 'bg-white/5 border-white/10 text-white hover:bg-white/10' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}>
                    <ArrowRight size={24} />
                </button>
                <div className={`px-5 py-2 rounded-2xl border font-black text-xl flex items-center gap-2 ${isDark ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-green-100 text-green-700 border-green-200'}`}>
                    <Sparkles size={20} className="text-yellow-400" />
                    <span>نقط الشراء</span>
                </div>
                <div className="w-10"></div>
            </div>

            <div className="max-w-xl mx-auto px-4 space-y-6">

                {/* Rules Box */}
                <div className={`p-6 rounded-3xl border shadow-lg ${isDark ? 'bg-blue-900/10 border-blue-500/20' : 'bg-blue-50 border-blue-100'}`}>
                    <ul className="space-y-3 text-xs sm:text-sm font-bold text-center leading-relaxed">
                        <li>كل ما تشتري منتج معين بيجيلك النقاط المكتوبة في وصف المنتج بشرط إنك تشتري بـ 200 جنيه أو أكثر وده لتجميع النقاط.</li>
                        <li>لو استعملت نقاط في العملية مش هتاخد هدية النقاط بتاعتها.</li>
                        <li>أقصى عدد نقاط هتحصل عليه 50 نقطة في العملية وأقصى عدد تصرفه 200 نقطة.</li>
                        <li>لو حصلت على نقاط في العملية هتاخد نص النقاط في العملية اللي بعدها لو حققت الشروط، لكن لو عملت أكثر من اتنين هتروح عليك النقاط لحد ما يعدي 48 ساعة على أول عملية.</li>
                    </ul>
                </div>

                {/* Cooldown Timer */}
                <div className={`p-5 rounded-3xl flex flex-col items-center justify-center border shadow-md ${isDark ? 'bg-amber-500/10 border-amber-500/20' : 'bg-amber-100 border-amber-200'}`}>
                    <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-black text-amber-600 dark:text-amber-500">
                            {timeLeft ? "⏳ العدد التنازلي للحصول علي نقاط مره اخري" : "✅ متاح الآن للحصول على نقاط"}
                        </span>
                    </div>
                    {timeLeft ? (
                        <div className="text-4xl sm:text-5xl font-black text-amber-500 tracking-wider" style={{ direction: 'ltr' }}>
                            {String(timeLeft.h).padStart(2, '0')} : {String(timeLeft.m).padStart(2, '0')} : {String(timeLeft.s).padStart(2, '0')}
                        </div>
                    ) : (
                        <div className="text-xl font-black text-green-600">
                            متاح الآن
                        </div>
                    )}
                </div>

                {/* Balance Cards ... (unmodified) ... */}
                <div className="grid grid-cols-2 gap-4">
                    <div className={`p-5 rounded-3xl text-center border shadow-xl bg-gradient-to-br from-[#1e1b4b] to-[#312e81] text-white`}>
                        <div className="flex items-center justify-center gap-2 mb-3">
                            <span className="text-xl">🏆</span>
                            <h3 className="font-black">إجمالي النقاط</h3>
                        </div>
                        <p className="text-2xl sm:text-3xl font-black">نقطة {profile?.pointsBalance || 0}</p>
                    </div>
                    <div className={`p-5 rounded-3xl text-center border shadow-xl bg-gradient-to-br from-[#dc2626] to-[#ef4444] text-white`}>
                        <div className="flex items-center justify-center gap-2 mb-3">
                            <ShoppingCart size={18} />
                            <h3 className="font-black">النقاط المصروفة</h3>
                        </div>
                        <p className="text-2xl sm:text-3xl font-black">نقطة {spentPoints}</p>
                    </div>
                </div>

                {/* Transaction History Inline */}
                <div className="pt-6">
                    <div className="flex items-center justify-center gap-2 mb-6">
                        <div className={`px-8 py-3 rounded-full shadow-lg font-black text-white bg-[#1e1b4b] flex items-center gap-2`}>
                            <History size={20} className="rotate-180" />
                            <span>تاريخ العمليات</span>
                        </div>
                    </div>

                    <div className="space-y-4">
                        {loading ? (
                            <div className="flex justify-center p-10"><Loader2 className="animate-spin text-blue-500" size={32} /></div>
                        ) : transactions.length === 0 ? (
                            <div className={`p-10 rounded-3xl border-2 border-dashed text-center ${isDark ? 'border-white/5 bg-white/5 text-white/20' : 'border-slate-100 bg-slate-50 text-slate-400'}`}>
                                <p className="font-black text-lg">لا يوجد عمليات نقاط سابقة</p>
                                <p className="text-xs font-bold opacity-60">اطلب دلوقتي وابدأ اجمع نقاط! 🚀</p>
                            </div>
                        ) : (
                            transactions.map(tx => {
                                const isClickable = !!tx.orderId;
                                return (
                                    <div
                                        key={tx.id}
                                        onClick={() => {
                                            if (tx.orderId) {
                                                navigate(`/?profile=orders&order=${tx.orderId}`);
                                            }
                                        }}
                                        className={`p-5 rounded-3xl border flex items-center justify-between shadow-sm transition-all hover:scale-[1.02] ${isClickable ? 'cursor-pointer hover:border-blue-500/50 hover:shadow-md' : ''} ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-100'}`}
                                    >
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <p className={`font-black uppercase ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                                                    {tx.type === 'earn' ? 'كسب نقاط' : tx.type === 'spend' ? 'استخدام نقاط' : 'تعديل نقاط'}
                                                </p>
                                                {isClickable && (
                                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>عرض الطلب</span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <p className="font-mono text-[10px] opacity-40">#{tx.id.slice(-6).toUpperCase()}</p>
                                                <p className="text-[10px] font-bold text-slate-400">
                                                    {tx.date.toLocaleString('ar-EG', { dateStyle: 'medium', timeStyle: 'short' })}
                                                </p>
                                            </div>
                                            <p className={`text-[11px] font-bold mt-1 ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                                                {tx.reason}
                                            </p>
                                        </div>
                                        <div className="text-left" style={{ direction: 'ltr' }}>
                                            <div className="flex flex-col items-end">
                                                <div className={`font-black flex items-center justify-end gap-1 ${tx.type === 'earn' ? 'text-green-500' : 'text-red-500'}`}>
                                                    <span>{tx.type === 'earn' ? '+' : '-'}{tx.points}</span>
                                                    {tx.type === 'earn' ? <Sparkles size={14} /> : <ShoppingCart size={14} />}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};
